import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { endpoints } from "../api/ApiEndpoints";
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces";
import dateService from "../date-service/DateService";
import { translationService } from "../translation/TranslationService";
import { ActivityTypes, getActivityLabel } from "./ActivityPricesStorage";
import { reaction } from "mobx";
import { organizationSubscriptionService } from "../organization/OrganizationSubscription";
import { authentication } from "../authentication/Authentication";
import { api } from "../api/ApiService";

interface ShopActivityItems {
    items: ShopActivity[],
    totalCount: number
}

export enum FileStates {
    Success,
    Failed,
    Running,
    Deleted,
    None
}

export interface ShopActivity {
    fileId: number,
    guid: string,
    filename: string,
    services: ActivityTypes[],
    timestamp: string,
    expiration: string,
    status: FileStates;
}

const emptyShopActivity: ShopActivity = {
    filename: "",
    services: [ActivityTypes.VideoConversion],
    timestamp: "",
    fileId: 0,
    guid: "",
    expiration: "",
    status: FileStates.Running
}

const columns : Column[] = [
    {
        key: "filename",
        name: "shop.shop-history.filename",
        type: ColumnType.String
    },
    {
        key: "service",
        name: "shop.shop-history.app",
        type: ColumnType.String
    },
    {
        key: "timestamp",
        name: "shop.shop-history.timestamp",
        type: ColumnType.DateTime
    },
    {
        key: "expiration",
        name: "shop.shop-history.expiration",
        type: ColumnType.DateTime,
    },
    {
        key: "status",
        name: "shop.shop-history.status",
        type: ColumnType.String
    },
    {
        key: "outputLink",
        name: "shop.shop-history.appLink",
        type: ColumnType.String,
        containsLink: true,
    },
]

const ShopHistoryModel = types.model({
    serviceFilter: types.optional(types.frozen<undefined | ActivityTypes[]>(), undefined),
    stateFilter: types.optional(types.frozen<undefined | FileStates>(), undefined),
    history: types.optional(types.frozen<ShopActivity[]>(), [emptyShopActivity]),
    clickPath: types.optional(types.frozen<string>(), "")
})

export const ShopHistoryStorageModel = types
    .compose(ShopHistoryModel, DataGridStorageModel)
    .named("ShopHistoryStorageModel")
    .actions((self) => {
        return {
            deleteFiles: flow(function* (ids: number[]) {
                const endpoint = endpoints.get("mediafiles");
                yield getEnv(self).api.delete(endpoint, ids);
                self.deselectAll();
                yield self.load();
            }),
            mapResponse(response: ShopActivityItems) {
                self.history = response.items;
                self.gridColumns = columns;
                if (self.clickPath !== "") {
                    self.gridColumns = self.gridColumns.filter(c => c.key !== "outputLink")
                }
                self.data = {
                    totalCount: response.totalCount,
                    items: response.items.map(self.mapItem)
                }
            },
            mapItem(item: ShopActivity) {
                var displayValues : DisplayValues = {
                    filename: {value: item.filename, toDisplay: item.filename},
                    service: {value: item.services, toDisplay: translationService.translate(getActivityLabel(item.services))},
                    timestamp: {value: item.timestamp, toDisplay: dateService.formatDateTime(item.timestamp)},
                    outputLink: {
                        value: item.guid,
                        toDisplay: activityIsExpired(item) ?
                            translationService.translate("shop.shop-history.expired") : translationService.translate("shop.shop-histroy.link"),
                        path: createAppLink(item)
                    },
                    expiration: {value: item.expiration, toDisplay: dateService.formatDateTime(item.expiration)},
                    status: {value: item.status, toDisplay: translationService.translate(mapStatus(item.status))},
                    guid: {value: item.guid, toDisplay: item.guid}
                }
                return {
                    displayValues: displayValues,
                    id: item.fileId
                }
            }
        }
    })
    .actions((self) => ({
        mockup() {
            self.history = [];
            self.clickPath = "";
        },
        wasActivityExecutedBy(sa: ShopActivity, services: ActivityTypes[]) {
            if (sa.services.length != services.length) return false;
            var activitySet = sa.services.sort();
            var servicesSet = services.sort();
            return activitySet.every((value, index) => value === servicesSet[index]);
        },
        getServiceProgressCount(services: ActivityTypes[]) {
            if (authentication.anonymous) return 0;
            return self.history.filter(sa => this.wasActivityExecutedBy(sa, services)).length;
        }
    }))
    .views((self) => ({
        get endpoint() {
            var endpoint = endpoints.get("shop_history");
            endpoint.queryParams = self.getDataQuery();
            if (self.serviceFilter !== undefined) {
                var query = self.serviceFilter.map(s => "&service=" + s.toString());
                endpoint.queryParams += query.join("");
            }
            if (self.stateFilter !== undefined) endpoint.queryParams += "&state=" + self.stateFilter;
            return endpoint;
        },
        get isLoading() {
            return self.history[0] === emptyShopActivity;
        },
        get canLoadItems() {
            return !authentication.anonymous || getEnv(self).organizationSubscriptionService.key;
        },
        get cannotDelete() {
            if (self.selectedItems.length < 1) return true;
            console.log(cast(self.selectedObjects))
            return self.selectedObjects
                .some(sa => activityIsExpired(
                    {...emptyShopActivity, 
                        expiration: sa.displayValues.expiration.value,
                        status: sa.displayValues.status.value
            }));
        }
    }));

export function mapStatus(status: FileStates) {
    switch (status) {
        case FileStates.Success: return "shop.shop-history.success";
        case FileStates.Failed: return "shop.shop-history.failed";
        case FileStates.Running: return "shop.shop-history.running";
        case FileStates.Deleted: return "shop.shop-history.deleted";
        default: return "";
    }
}

export function createAppLink(activity: ShopActivity) {
    if (activityIsExpired(activity)) return "";
    if (activity.services.filter(a => a === ActivityTypes.VideoAnalysis || ActivityTypes.HighlightDetection).length === 2) return "/analysis-and-segmentation?guid=" + activity.guid;
    if (activity.services.filter(a => a === ActivityTypes.VideoConversion).length === 1) return "/video-conversion?guid=" + activity.guid;
    if (activity.services.filter(a => a === ActivityTypes.VideoAnalysis).length === 1) return "/video-analysis?guid=" + activity.guid;
    if (activity.services.filter(a => a === ActivityTypes.HighlightDetection).length === 1) return "/video-segmentation?guid=" + activity.guid;
    if (activity.services.filter(a => a === ActivityTypes.VideoOcr).length === 1) return "/inspection-insights?guid=" + activity.guid;
}

function activityIsExpired(activity: ShopActivity) {
    return new Date(activity.expiration) < new Date() || activity.status === FileStates.Deleted
}

export type ShopHistoryStorage = Instance<typeof ShopHistoryStorageModel>;

export const shopHistoryStorage = ShopHistoryStorageModel.create({stateFilter: FileStates.Running},{api, organizationSubscriptionService})

reaction(
    () => organizationSubscriptionService.key,
    async () => {
      if (authentication.anonymous) {
        shopHistoryStorage.mockup();
      } else {
        await shopHistoryStorage.load();
      }
    }
  );

reaction(
    () => authentication.anonymous,
    async () => {
      if (!authentication.anonymous) await shopHistoryStorage.load();
    }
  )
